import { FunctionComponent, Suspense, useEffect, useState } from 'react';
import { Navigation, TupasLogin, NavigationProps, Div } from '@gaiads/telia-react-component-library';
import { CartProps } from '@gaiads/telia-react-component-library/build/types/components/Navigation/Cart/Cart';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router';
import { useRouteMatch } from 'react-router-dom';
import { UserRole } from '@cdaas/api-clients/dist/asset-user-openapi';

import './Header.scss';
import { checkIsSelectedOrganizationOwn, createAuthenticationUrl, getUserInitials, hasUserARequiredRole, setHeaderLanguage } from '../../../utils';
import { RootState } from '../../../store/reducers';
import * as actions from './../../../store/actions';
import { SupportedLanguage } from '../../../utils/constants';

type Language = {
  text: string;
  active: boolean;
  onClick: () => void;
};

type MenuItemArray = NavigationProps['menuItems'];

const Header: FunctionComponent = () => {
  const { t, i18n } = useTranslation('layoutComponents');
  const location = useLocation();
  const history = useHistory();
  const match = useRouteMatch({
    path: '/onboarding',
    strict: false,
    sensitive: false,
  });
  const loggedIn = useSelector((state: RootState) => state.authentication.authenticated);
  const usersName = useSelector((state: RootState) => state.authentication.user?.name);
  const product = useSelector((state: RootState) => state.shop.cart);
  const orderResponse = useSelector((state: RootState) => state.shop.orderResponse);
  const numberOfPendingApprovals = useSelector((state: RootState) => state.authentication.pendingApprovals);
  const ownOrganizationId = useSelector((state: RootState) => state.authentication.user?.organization?.id);
  const selectedOrganizationId = useSelector((state: RootState) => state.authentication.selectedOrganization?.id);

  const [languages, setLanguages] = useState<Language[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItemArray>([]);

  const dispatch = useDispatch();
  const logout = () => dispatch(actions.executeLogout());

  const isSelectedOrganizationOwn = checkIsSelectedOrganizationOwn(ownOrganizationId, selectedOrganizationId);

  useEffect(() => {
    const newMenuItems: MenuItemArray = [];
    if (loggedIn) {
      newMenuItems.push({
        text: t('Header.menuItems.home'),
        url: '/home',
      });
    }

    if (hasUserARequiredRole([UserRole.User, UserRole.UserLite])) {
      const userDevicesMenuItem = {
        text: t('Header.menuItems.myDevices'),
        menuItems: [
          {
            text: t('Header.menuItems.myOrders'),
            url: '/orders',
          },
        ],
      };
      if (isSelectedOrganizationOwn) {
        userDevicesMenuItem.menuItems.unshift({
          text: t('Header.menuItems.myDevices'),
          url: '/myassets',
        });
      }
      newMenuItems.push(userDevicesMenuItem);
    }

    if (hasUserARequiredRole([UserRole.Admin])) {
      newMenuItems.push({
        text: t('Header.menuItems.settingsAndOnboarding'),
        menuItems: [
          {
            text: t('Header.menuItems.companySettings'),
            url: '/organization',
          },
          {
            text: t('Header.menuItems.groups'),
            url: '/onboarding/usergroups',
          },
          {
            text: t('Header.menuItems.usersAndOnboarding'),
            url: '/users',
          },
        ],
      });
    }

    if (hasUserARequiredRole([UserRole.Admin, UserRole.Approver])) {
      const menuItems = [];

      if (hasUserARequiredRole([UserRole.Approver])) {
        menuItems.push({
          text: t('Header.menuItems.ordersForApproval'),
          url: '/approval',
        });
      }

      if (hasUserARequiredRole([UserRole.Admin])) {
        menuItems.push({
          text: t('Header.menuItems.orderOverview'),
          url: '/organizationorders',
        });
      }

      newMenuItems.push({
        text: t('Header.menuItems.organizationOrders'),
        menuItems,
      });
    }

    if (hasUserARequiredRole([UserRole.Admin])) {
      newMenuItems.push({
        text: t('Header.menuItems.devicesAndReports'),
        url: '/organizationassets',
      });
    }

    setMenuItems(newMenuItems);
  }, [loggedIn, i18n.language, t, numberOfPendingApprovals, isSelectedOrganizationOwn]);

  useEffect(() => {
    const changeLanguage = (lng: SupportedLanguage): void => {
      i18n.changeLanguage(lng);
      setHeaderLanguage(lng);
      setLanguages((prevLanguages: Language[]) => {
        return prevLanguages.map((lang: Language) => {
          lang.active = lang.text.toLocaleLowerCase() === lng.toLocaleLowerCase();
          return lang;
        });
      });
    };
    const defaultLanguages: Language[] = [
      {
        text: 'EN',
        active: true,
        onClick: () => changeLanguage('en'),
      },
      {
        text: 'NO',
        active: false,
        onClick: () => changeLanguage('no'),
      },
    ];
    const selectedLang = i18n.language.toLocaleLowerCase().slice(0, 2);
    setHeaderLanguage(selectedLang);
    setLanguages(
      defaultLanguages.find(lang => lang.text.toLowerCase() === selectedLang)
        ? defaultLanguages.map((lang: Language) => {
            lang.active = lang.text.toLowerCase() === selectedLang;
            return lang;
          })
        : defaultLanguages,
    );
  }, [i18n]);

  const logo: NavigationProps['logo'] = {
    link: '/home',
    linkAriaLabel: 'Home',
  };

  const login: NavigationProps['login'] = {
    headingText: '',
    tabs: [
      {
        tabTitle: t('Header.loginMenu.login'),
        content: (
          <TupasLogin
            loginButtonLabel={t('Header.loginMenu.login')}
            onLoginButtonClick={() => {
              window.location.href = createAuthenticationUrl(location.pathname);
            }}
          />
        ),
      },
    ],
    onLogout: () => {
      history.push('/authentication/logout');
      logout();
    },
  };

  let userMenu: NavigationProps['userMenu'] = false;

  if (loggedIn) {
    userMenu = {
      username: usersName || '',
      userMenuLinks: [],
      logoutButtonLabel: t('Header.logoutLink'),
      userInitials: getUserInitials(usersName ?? undefined),
    };
  }

  const showPendingApprovalsIcon = !!numberOfPendingApprovals && hasUserARequiredRole([UserRole.Approver]);

  const getCartProps = (): CartProps | undefined => {
    if (product && !orderResponse) {
      return { amount: 1, linkAriaLabel: 'Link to product cart', link: '/shop/checkout' };
    }
    return undefined;
  };

  return (
    <Suspense fallback="loading">
      <Div className={['navigation-wrapper', showPendingApprovalsIcon && 'pending-approvals']}>
        <Navigation
          cart={getCartProps()}
          logo={logo}
          languages={languages}
          login={login}
          menuItems={match?.path === '/onboarding' ? [] : menuItems}
          userMenu={userMenu}
        />
      </Div>
    </Suspense>
  );
};

export default Header;
